/* @flow */
import Vue from 'vue'
import axios from 'axios'
import normalize from 'jsonapi-normalizer'
import oPath from 'object-path'
import query from 'query-string'

import {REMOVE_SESSION} from './session'
import {
  computePaginateSortFilterParams,
  computedCollectionCount,
  nullifyBlankStrings,
} from '@/utils/api'
import {NEW_NOTIFICATION, REPLACE_NOTIFICATION} from './notifications'
import type {JsonApiEntity} from '@/store'

export const REPLACE_SERVICE_API_KEYS = 'REPLACE_SERVICE_API_KEYS'
export const ADD_SERVICE_API_KEYS = 'ADD_SERVICE_API_KEYS'
export const MODIFY_SERVICE_API_KEY = 'MODIFY_SERVICE_API_KEY'
export const REMOVE_SERVICE_API_KEY = 'REMOVE_SERVICE_API_KEY'
export const SET_SERVICE_API_KEYS_TOTAL = 'SET_SERVICE_API_KEYS_TOTAL'

export const FETCH_SERVICE_API_KEYS = 'FETCH_SERVICE_API_KEYS'
export const FETCH_SERVICE_API_KEY = 'FETCH_SERVICE_API_KEY'
export const CREATE_SERVICE_API_KEY = 'CREATE_SERVICE_API_KEY'
export const UPDATE_SERVICE_API_KEY = 'UPDATE_SERVICE_API_KEY'
export const DELETE_SERVICE_API_KEY = 'DELETE_SERVICE_API_KEY'

const initialState = () => ({
  entities: { },
  statistics: {
    total: null,
  },
})

export default {
  state: initialState(),
  getters: {
    service_api_keys: ({entities = {}}: Object) => entities,
    service_api_key: (state: Object, getters: Object) => (id: string) => {
      return getters.service_api_keys[id] 
    }
  },
  mutations: {
    RESET_STATE(state: Object) {
      Object.assign(state, initialState())
    },
    [REMOVE_SESSION](state: Object) {
      state.entities = initialState().entities
    },
    [REPLACE_SERVICE_API_KEYS](state: Object, {entities = {}}: Object) {
      state.entities = entities
    },
    [ADD_SERVICE_API_KEYS](state: Object, payload: Object) {
      state.entities = {
        ...state.entities,
        ...payload.entities,
      }
    },
    [MODIFY_SERVICE_API_KEY](state: Object, payload: Object) {
      state.entities[payload.id] = {
        ...state.entities[payload.id],
        ...payload,
      }
    },
    [REMOVE_SERVICE_API_KEY](state: Object, payload: Object) {
      Vue.delete(state.entities, payload.id)
    },
    [SET_SERVICE_API_KEYS_TOTAL](state: Object, payload: Object) {
      state.statistics.total = payload.total
    },
  },
  actions: {
    [FETCH_SERVICE_API_KEYS]: async function(
      {commit, dispatch}: Object,
      payload: Object = {}
    ) {
      try {
        let pathstring = '/service_api_keys'

        const {data} = await axios.get(pathstring, {
          params: {
            ...computePaginateSortFilterParams(payload),
          },
        })

        const {pagination} = data.meta
        let { entities } = normalize(data) 

        if (entities == null) entities = { service_api_keys: {} } 
        if (entities.service_api_keys == null) entities.service_api_keys = {} 
        commit(SET_SERVICE_API_KEYS_TOTAL, {total: pagination.collection_count})
        commit(REPLACE_SERVICE_API_KEYS, {
          entities: entities.service_api_keys,
        })
        
        return {
          success: true,
          // Return fetched data as flat array
          // items: data.data.map(item => item.attributes),
          items: Object.values(entities.service_api_keys),
          collectionCount: computedCollectionCount({
            endRow: payload.endRow,
            filteredCount: pagination.filtered_count,
            collectionCount: pagination.collection_count,
          }),
        }
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load service-api-keys ',
          detail: err.message,
          color: 'error',
        })
      }
      return {success: false}
    },
    [FETCH_SERVICE_API_KEY]: async function({commit, dispatch}: Object, payload: Object) {
      if (payload.id == null) return { success: false, status: 404 }
      try {
        const {data} = await axios.get(`/service_api_keys/${payload.id}`)
        const {entities} = normalize(data)
        commit(ADD_SERVICE_API_KEYS, {entities: entities.service_api_keys})
        return {success: true, status: 200}
      } catch (err) {
        const status = oPath.get(err, 'response.status')
        if (status !== 404) {
          await dispatch(NEW_NOTIFICATION, {
            text: 'Failed to load service-api-key ',
            detail: err.message,
            color: 'error',
          })
        }
        return {success: false, status}
      }
    },
    [CREATE_SERVICE_API_KEY]: async function(
      {commit, dispatch}: Object,
      payload: JsonApiEntity
    ) {
      try {
        if (payload.attributes.enabled == null) payload.attributes.enabled = false
        const {data} = await axios.post('/service_api_keys', {
          data: {
            type: 'service_api_keys',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        
        commit(ADD_SERVICE_API_KEYS, {entities: entities.service_api_keys})
        if (!payload.suppressNotifications) {
          await dispatch(NEW_NOTIFICATION, {
            text: 'Saved new service api key!',
            color: 'success',
          })
        }
        return data.data.attributes
      } catch (err) {
        await dispatch(NEW_NOTIFICATION, {
          text: 'Failed to save service-api-key ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [UPDATE_SERVICE_API_KEY]: async function({commit, dispatch}: Object, payload: Object) {
      let notificationId
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: 'Saving...',
          color: 'secondary',
          undoable: true,
        })
      }
      nullifyBlankStrings(payload.attributes)
      try {
        const {data} = await axios.patch(`/service_api_keys/${payload.id}`, {
          data: {
            type: 'service_api_keys',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(MODIFY_SERVICE_API_KEY, entities.service_api_keys[payload.id])
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: 'Saved!',
            color: 'success',
            undoable: true,
          })
        }
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to update service-api-key ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [DELETE_SERVICE_API_KEY]: async function({commit, dispatch}: Object, payload: Object) {
      let notificationId
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: `Deleting service-api-key...`,
          color: 'secondary',
        })
      }

      try {
        await axios.delete(
          `/service_api_keys/${payload.id}?${query.stringify(payload.options)}`
        )
        commit(REMOVE_SERVICE_API_KEY, payload)
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: `Deleted service-api-key!`,
            color: 'success',
          })
        }
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: `Failed to delete service-api-key `,
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/service_api_keys.js